import {v4 as guid} from 'uuid';
import moment from 'moment';
import {paymentsSlice, callTypes} from './paymentsSlice';
import * as requestFromServer from './paymentsCrud';
import {showGlobalMessage} from '../../_common/actions/globalMessage/showGlobalMessage';
import {showProgressBarPopUp} from '../../_common/actions/progressBarPopUp/showProgressBarPopUp';
import {hideProgressBarPopUp} from '../../_common/actions/progressBarPopUp/hideProgressBarPopUp';
import {messages as M} from '../../../../_metronic/i18n/messages';
import {downloadFile} from '../../../../utils';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {logUserAction} from '../../../../utils';
import {pages as P} from '../../_common/constants/pages';
import {subjects as S} from '../../_common/constants/subjects';
import {actionTypes as T} from '../../_common/constants/actionTypes';
import {logMessages as L} from '../../_common/constants/logMessages';

export const getPaymentsListIDPrefix = 'getPaymentsList';
const {actions} = paymentsSlice;

const getPaymentsList = storeMethod => queryParams => dispatch => {
  dispatch(actions.startCall({callType: callTypes.list}));

  const requestID = `${getPaymentsListIDPrefix}-${guid()}`;
  const cancellationHandlers = RequestCancellationManager.createCancellationHandlers(requestID);

  return requestFromServer
    .findPayments(queryParams, cancellationHandlers)
    .then(response => {
      const {errorCode, data: {totalPaymentCount: totalCount, paymentsList, pageCount}} = response.data;
      if (errorCode !== 0) throw new Error();

      dispatch(storeMethod({
        totalCount,
        pageCount,
        entities: paymentsList
      }));
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'Can\'t find payments';
      dispatch(actions.catchError({error, callType: callTypes.list}));
    })
    .finally(() => {
      RequestCancellationManager.forgetRequest(requestID);
    });
};

export const uploadFileAction = (file) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));
  dispatch(showProgressBarPopUp(M.UPLOADING_FILE));

  const formData = new FormData();
  formData.set('content', file);

  const delay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  return requestFromServer
    .uploadFile({formData})
    .then(response => {
      const {errorCode} = response.data;
      logUserAction(
        P.AUTOMATED_PAYMENTS,
        S.PAYMENT_FILE,
        T.UPLOAD,
        L.PAYMENTS_UPLOAD_FILE(file?.name),
        errorCode
      );

      return errorCode;
    })
    .then(() => delay(3000))
    .then(() => {
      dispatch(hideProgressBarPopUp());
    })
    .then(() => {
      const paramsFromURL = {};
      const requestedPageNumber = 1;
      dispatch(getPayments({...paramsFromURL, requestedPageNumber}));
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'can\'t upload file!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const updateManualStatusAction = (paymentRequestID, agentID, paymentRequestDate) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .updateManualStatus({paymentRequestID})
    .then(response => {
      const {errorCode, data} = response.data;
      logUserAction(
        P.AUTOMATED_PAYMENTS,
        S.PAYMENT,
        T.EDIT,
        L.PAYMENTS_UPDATE_MANUAL_STATUS(agentID, paymentRequestDate ? moment(paymentRequestDate).format('DD-MM-YYYY') : '',),
        errorCode
      );
      if (errorCode === 0) {
        dispatch(actions.statusUpdated({
          paymentRequestID: paymentRequestID,
          updatedStatusID: data.statusID,
          updatedStatusText: data.statusText,
          lastStatusUpdateDate: data.lastStatusUpdateDate,
        }));
        dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      } else {
        throw new Error();
      }

      return errorCode;
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'can\'t update manual status!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const resendPaymentAction = (paymentRequestID, agentID, paymentRequestDate) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .resendPayment({paymentRequestID})
    .then(response => {
      const {errorCode, data} = response.data;
      logUserAction(
        P.AUTOMATED_PAYMENTS,
        S.PAYMENT,
        T.EDIT,
        L.PAYMENTS_RESEND(agentID, paymentRequestDate ? moment(paymentRequestDate).format('DD-MM-YYYY') : '',),
        errorCode
      );
      if (errorCode === 0) {
        dispatch(actions.statusResent({
          paymentRequestID: paymentRequestID,
          updatedStatusID: data.statusID,
          updatedStatusText: data.statusText,
          lastStatusUpdateDate: data.lastStatusUpdateDate,
        }));
        dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      } else {
        throw new Error();
      }

      return errorCode;
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'can\'t resend payment!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const addBirthDateAction = (agentID, birthDate) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .addBirthDateToAgent({agentID, birthDate})
    .then(response => {
      const {errorCode} = response.data;
      logUserAction(
        P.AUTOMATED_PAYMENTS,
        S.USER_INFORMATION,
        T.EDIT,
        L.PAYMENTS_ADD_AGENT_BIRTH_DATE(agentID, birthDate ? moment(birthDate).format('DD-MM-YYYY') : ''),
        errorCode
      );
      if (errorCode === 0) {
        dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      } else {
        throw new Error();
      }

      return errorCode;
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'Can\'t set agent\'s birth date!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const downloadPaymentConfirmAction = ({paymentRequestID}) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .downloadPaymentConfirm({paymentRequestID})
    .then(response => {
      const {errorCode, data} = response.data;
      if (errorCode !== 0) throw new Error();

      const fileName = `${data.fileName}`;

      try {
        downloadFile(`data:;base64,${data.file}`, fileName);
      } catch (exc) {
        console.error('Save Blob method failed with the following exception.');
        console.error(exc);
      }
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'Can\'t get file!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const exportPaymentsFileAction = queryParams => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .getPaymentsFile(queryParams)
    .then(response => {
      const {errorCode, data} = response.data;
      if (errorCode !== 0) throw new Error();

      return data.paymentsList;
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'Can\'t get file!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const updateAmountAction = ({paymentRequestID, debtAmount, insuranceAmount, agentID, paymentRequestDate}) => dispatch => {
  dispatch(actions.startCall({callType: callTypes.action}));

  return requestFromServer
    .updateAmount({paymentRequestID, debtAmount, insuranceAmount})
    .then(response => {
      const {errorCode} = response.data;
      logUserAction(
        P.AUTOMATED_PAYMENTS,
        S.PAYMENT,
        T.EDIT,
        L.PAYMENTS_EDIT_AMOUNT(
          agentID,
          paymentRequestDate ? moment(paymentRequestDate).format('DD-MM-YYYY') : '',
          debtAmount,
          insuranceAmount
        ),
        errorCode
      );

      if (errorCode === 0) {
        dispatch(actions.amountUpdated({
          paymentRequestID: paymentRequestID,
          debtAmount: debtAmount,
          insuranceAmount: insuranceAmount,
          totalAmount: debtAmount + insuranceAmount,
        }));
        dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      } else {
        throw new Error();
      }

      return errorCode;
    })
    .catch(error => {
      console.error({error});
      error.clientMessage = 'Can\'t update amount!';
      dispatch(actions.catchError({error, callType: callTypes.action}));
    });
};

export const getPayments = getPaymentsList(actions.paymentsFetched);
export const getNextPayments = getPaymentsList(actions.paymentsAppended);
